import React from "react";
import { Button, Empty, Flex, Input, Typography, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  useLazyGetUpdatedSummaryQuery,
  WsProduct,
  WsProductSummary,
} from "../../../api/webshop/wsProductsApi";

const { Text } = Typography;

function formatValue(value?: number) {
  if (value == null) return "";
  return String(value);
}

function formatPrice(value?: number) {
  if (value == null) return "";
  return value.toFixed(2);
}

function SummaryFields({ summary }: { summary: WsProductSummary }) {

  return (
    <>
      <Text strong>Data</Text>
      <Flex gap={12}>
        <Input
          addonBefore="Min (GB)"
          disabled
          value={formatValue(summary.data_min)}
          style={{ flex: 1 }}
        />
        <Input
          addonBefore="Max (GB)"
          disabled
          value={formatValue(summary.data_max)}
          style={{ flex: 1 }}
        />
      </Flex>

      <Text strong>Validity</Text>
      <Flex gap={12}>
        <Input
          addonBefore="Min (Days)"
          disabled
          value={formatValue(summary.validity_min)}
          style={{ flex: 1 }}
        />
        <Input
          addonBefore="Max (Days)"
          disabled
          value={formatValue(summary.validity_max)}
          style={{ flex: 1 }}
        />
      </Flex>

      <Text strong>Price</Text>
      <Flex gap={12}>
        <Input
          addonBefore="Min ($)"
          disabled
          value={formatPrice(summary.price_min)}
          style={{ flex: 1 }}
        />
        <Input
          addonBefore="Max ($)"
          disabled
          value={formatPrice(summary.price_max)}
          style={{ flex: 1 }}
        />
      </Flex>
      <Text strong>Calls</Text>
      <Flex gap={12}>
        <Input
          addonBefore="Min ($)"
          disabled
          value={formatValue(summary.calls_min)}
          style={{ flex: 1 }}
        />
        <Input
          addonBefore="Max ($)"
          disabled
          value={formatValue(summary.calls_max)}
          style={{ flex: 1 }}
        />
      </Flex>
    </>
  );
}

export default function ProductSummaryTab(props: {
  p: WsProduct;
  setP?: (p: WsProduct) => void;
}) {
  const summary = props.p.summary;
  const [getUpdatedSummary, { isFetching }] = useLazyGetUpdatedSummaryQuery();

  if (!props.p.productId) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 280 }}>
        <Empty description="Save the product first to view variant summary." />
      </Flex>
    );
  }

  const handleRecalculate = () => {
    getUpdatedSummary(props.p.productId)
      .unwrap()
      .then((res) => {
        const nextSummary: WsProductSummary | undefined =
          res && typeof res === "object" && "summary" in res
            ? (res as WsProduct).summary
            : (res as WsProductSummary);

        if (nextSummary && props.setP) {
          props.setP({ ...props.p, summary: nextSummary });
        }
        message.success("Summary recalculated");
      })
      .catch((err) => {
        console.error("API error:", err);
        message.error(
          err?.data?.message || err?.message || "Failed to recalculate summary",
        );
      });
  };

  return (
    <Flex vertical gap={12} style={{ padding: "12px 0" }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
        <Text strong>Product Summary</Text>
        <Button
          icon={<ReloadOutlined />}
          loading={isFetching}
          onClick={handleRecalculate}
        >
          Re-calculate Summary
        </Button>
      </Flex>

      {summary ? (
        <SummaryFields summary={summary} />
      ) : (
        <Empty description="No summary data available for this product." />
      )}
    </Flex>
  );
}
